import { UserPublisherService } from '@shared/services/publishers/user-publisher.service';
import { HttpEventType, HttpProgressEvent } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { typesUploading } from '@shared/constants/types-uploading';
import { AlertService } from '@shared/services/alert.service';
import { ApiService } from '@shared/services/api.service';
import { DevicePublisherService } from '@shared/services/publishers/device-publisher.service';
import { Subscription } from 'rxjs';
import { ErrorCode } from '@shared/enums/error-code.enum';

@Component({
  selector: 'app-upload-file-to-server',
  templateUrl: './upload-file-to-server.component.html',
  styleUrls: ['./upload-file-to-server.component.scss'],
})
export class UploadFileToServerComponent implements OnDestroy {
  @Output() fileUploaded = new EventEmitter<{
    isSuccess: boolean;
    hideModal: boolean;
    data: any;
  }>();
  @Input() methodName: typesUploading = typesUploading.uploadFileToDevice;
  @Input() deviceData!: { path: string; deviceId: string; jobId?: string };
  @Input() extensions = ['.rcode', '.rcodex', '.gcode'];
  @Input() disabled = false;
  @Input() maxFileSize = 1024 * 1024 * 1024;

  currentStep = 1;
  progress = 0;
  subscription: Subscription = new Subscription();
  progressSubscription: Subscription = new Subscription();
  showCancelUpload = true;
  private uploadId: string | undefined = undefined;

  fileName: string = '';

  constructor(
    private api: ApiService,
    private alertService: AlertService,
    private translate: TranslateService,
    private devicePublisherService: DevicePublisherService,
    private userPublisherService: UserPublisherService
  ) {}

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }

  overrideDefault(event: any) {
    event.preventDefault();
    event.stopPropagation();
  }

  uploadFile(event: any) {
    event.preventDefault();
    var droppedFiles = event.target.files || event.dataTransfer.files;

    if (droppedFiles.length > 1) {
      this.alertService.danger(ErrorCode.TOO_MANY_FILES_SELECTED, 4000);
      return;
    }

    this.fileName = droppedFiles[0].name;

    const fileExtension = this.fileExtension(this.fileName);
    if (!fileExtension || !this.extensions.includes(fileExtension)) {
      this.alertService.danger(
        this.translate.instant('alerts.incorrect-file-extension', {
          extensions: this.extensions.join(', '),
        }),
        5000
      );
      return;
    }

    if (droppedFiles[0].size > this.maxFileSize) {
      this.alertService.danger('alerts.file-size-too-large', 5000);
      return;
    }

    this.currentStep = 2;
    this[this.methodName](droppedFiles[0]);
  }

  protected uploadFileToGoogleDrive(file: File): void {
    const formData = new FormData();

    formData.append('Path', this.deviceData.path);
    formData.append('File', file);

    this.subscription = this.api.postUploadGoogleDriveFile(formData).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.calculateProgressToDatabase(event);
          return;
        }

        if (event.type === HttpEventType.Response) {
          this.progress = 100;

          this.alertService.success(
            this.translate.instant('alerts.file-upload-server-success', {
              fileName: this.fileName,
            }),
            4000
          );
          this.fileUploaded.emit({
            isSuccess: true,
            hideModal: true,
            data: event.body,
          });
        }
      },
      error: (err) => {
        this.fileUploaded.emit({
          isSuccess: false,
          hideModal: false,
          data: null,
        });
      },
    });
  }

  uploadFileToAWS(file: any) {
    this.currentStep = 2;
    const fd = new FormData();
    fd.append('File', file);

    this.subscription = this.api.postFileUploadPreview(fd).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.calculateProgressToDatabase(event);
          return;
        }

        if (event.type === HttpEventType.Response) {
          this.alertService.success(
            this.translate.instant('alerts.file-upload-server-success', {
              fileName: this.fileName,
            }),
            4000
          );
          this.fileUploaded.emit({
            isSuccess: true,
            hideModal: false,
            data: event.body,
          });
        }
      },
      error: (err) => {
        this.fileUploaded.emit({
          isSuccess: false,
          hideModal: false,
          data: null,
        });
      },
    });
  }

  uploadFileToDevice(file: any) {
    const fd = new FormData();

    fd.append('Path', this.deviceData.path);
    fd.append('File', file);

    this.subscription = this.api
      .postFileUpload(fd, this.deviceData.deviceId)
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.calculateProgressToDatabase(event);
            return;
          }

          if (event.type === HttpEventType.Response) {
            this.uploadId = event?.body?.uploadId;
            this.uploadFileToDeviceProgress(file.name);
          }
        },
        error: (err) => {
          this.showCancelUpload = true;
          this.fileUploaded.emit({
            isSuccess: false,
            hideModal: true,
            data: null,
          });
        },
      });
  }

  uploadFileToPrintout(file: any) {
    const fd = new FormData();
    fd.append('File', file);

    this.subscription = this.api
      .postFileUploadToPrintout(this.deviceData.jobId!, fd)
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.calculateProgressToDatabase(event);
            return;
          }

          if (event.type === HttpEventType.Response) {
            this.progress = 100;
            this.showCancelUpload = false;
            this.alertService.success(
              this.translate.instant('alerts.file-upload-server-success', {
                fileName: this.fileName,
              }),
              4000
            );
            this.fileUploaded.emit({
              isSuccess: true,
              hideModal: true,
              data: null,
            });
          }
        },
        error: (err) => {
          this.showCancelUpload = true;
          this.fileUploaded.emit({
            isSuccess: false,
            hideModal: true,
            data: null,
          });
        },
      });
  }

  private calculateProgressToDatabase(event: HttpProgressEvent) {
    this.progress = Math.round((25 * event.loaded) / (event.total ?? 1));

    if (this.progress === 25) {
      this.subscription.add(
        this.userPublisherService
          .getFileUploadingProgressObservable()
          .subscribe((r) => {
            this.progress = 25 + Math.round(0.25 * r);
          })
      );
    }
  }

  private uploadFileToDeviceProgress(fileName: string) {
    if (!this.uploadId) {
      return;
    }

    this.progressSubscription = this.devicePublisherService
      .getDeviceUploadingProgressObservable(
        this.deviceData.deviceId,
        this.uploadId
      )
      .subscribe((res) => {
        if (res.errorCode === 0 && res.progress === 100) {
          this.progress = 100;
          this.showCancelUpload = false;
          this.alertService.success(
            this.translate.instant('alerts.file-upload-device-success', {
              fileName: fileName,
            }),
            4000
          );
          this.fileUploaded.emit({
            isSuccess: true,
            hideModal: true,
            data: null,
          });
        } else if (res.errorCode === 2) {
          this.showCancelUpload = false;
          // this.alertService.success('alerts.cancel-upload-success', 5000);
          this.alertService.danger('errors.4004', 5000);
          this.fileUploaded.emit({
            isSuccess: true,
            hideModal: true,
            data: null,
          });
        } else {
          this.progress = Math.round(50 + res.progress / 2);
        }
      });
  }

  private fileExtension(fileName: string): string | null {
    let fileExtension: string;
    const index = fileName.lastIndexOf('.');
    if (index > 0) {
      fileExtension = fileName.substring(index, fileName.length);
      return fileExtension;
    }
    return null;
  }

  cancelUploadFile() {
    this.showCancelUpload = false;
    this.subscription.unsubscribe();

    if (this.deviceData && this.uploadId) {
      this.api
        .postCancelUpload(this.deviceData.deviceId, this.uploadId)
        .subscribe({
          next: () =>
            this.alertService.success('alerts.cancel-upload-success', 5000),
          error: (err) => {},
        });
    } else {
      this.alertService.success('alerts.cancel-upload-success', 5000);
    }

    this.fileUploaded.emit({ isSuccess: true, hideModal: false, data: null });
  }

  clearData() {
    this.currentStep = 1;
    this.progress = 0;
    this.uploadId = undefined;
    this.subscription = new Subscription();
    this.showCancelUpload = true;
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }
}
